"use client";

import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

const TRIGGER_DISTANCE = 70;
const MAX_PULL = 120;
const RESISTANCE = 0.5;

function isTauri(): boolean {
  return typeof window !== "undefined" && !!(window as unknown as { __TAURI__?: unknown }).__TAURI__;
}

function isEditableTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
}

function findScrollableAncestor(start: HTMLElement | null): HTMLElement | null {
  let node: HTMLElement | null = start;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    const canScroll =
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      node.scrollHeight > node.clientHeight;
    if (canScroll) return node;
    node = node.parentElement;
  }
  return null;
}

export function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef<number | null>(null);
  const trackingRef = useRef(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Tauri デスクトップアプリでは無効
    if (isTauri()) return;
    // タッチデバイスでなければ無効
    if (typeof window === "undefined") return;
    if (!("ontouchstart" in window)) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (refreshing) return;
      if (e.touches.length !== 1) return;
      if (isEditableTarget(e.target)) return;

      const container = findScrollableAncestor(e.target as HTMLElement);
      if (!container) {
        // スクロール可能なコンテナがない場合は受け付けない（誤動作防止）
        return;
      }
      if (container.scrollTop > 0) return;

      startYRef.current = e.touches[0].clientY;
      scrollContainerRef.current = container;
      trackingRef.current = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!trackingRef.current) return;
      if (startYRef.current === null) return;
      const container = scrollContainerRef.current;
      if (!container) return;

      // スクロールが進んでしまったら追跡終了
      if (container.scrollTop > 0) {
        trackingRef.current = false;
        startYRef.current = null;
        setPull(0);
        return;
      }

      const deltaY = e.touches[0].clientY - startYRef.current;
      if (deltaY <= 0) {
        setPull(0);
        return;
      }

      // 抵抗をかけて引っ張り感を出す
      const pulled = Math.min(MAX_PULL, deltaY * RESISTANCE);
      setPull(pulled);

      // 引っ張っている間はブラウザのスクロールやリフレッシュを抑止
      if (deltaY > 5 && e.cancelable) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      if (!trackingRef.current) return;
      trackingRef.current = false;
      startYRef.current = null;
      scrollContainerRef.current = null;

      if (pull >= TRIGGER_DISTANCE) {
        setRefreshing(true);
        // 少しだけアニメーションを見せてからリロード
        window.setTimeout(() => {
          window.location.reload();
        }, 150);
      } else {
        setPull(0);
      }
    };

    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    document.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [pull, refreshing]);

  const progress = Math.min(1, pull / TRIGGER_DISTANCE);
  const visible = pull > 0 || refreshing;

  return (
    <div
      aria-hidden
      className={cn(
        "fixed inset-x-0 top-0 z-[100] flex justify-center pointer-events-none",
        "transition-opacity",
        visible ? "opacity-100" : "opacity-0"
      )}
      style={{
        transform: `translateY(${Math.max(0, pull - 40)}px)`,
        paddingTop: "env(safe-area-inset-top, 0px)",
      }}
    >
      <div
        className={cn(
          "mt-2 w-10 h-10 rounded-full bg-card shadow-md border border-border/50",
          "flex items-center justify-center text-foreground"
        )}
      >
        <RefreshCw
          className={cn("w-5 h-5", refreshing && "animate-spin")}
          style={{
            transform: refreshing ? undefined : `rotate(${progress * 360}deg)`,
            opacity: 0.4 + progress * 0.6,
          }}
        />
      </div>
    </div>
  );
}
